'use client';

import { useEffect } from 'react';

// Define Intercom function types
type IntercomFunction = {
  (command: 'reattach_activator'): void;
  (command: 'update', settings: Record<string, unknown>): void;
  (command: 'shutdown'): void;
  (command: string, ...args: unknown[]): void;
  c: (args: unknown[]) => void;
  q: unknown[][];
};

declare global {
  interface Window {
    Intercom?: IntercomFunction;
    intercomSettings?: Record<string, unknown>;
  }
}

interface IntercomProps {
  appId: string;
  // Optional user data that can be passed to Intercom
  user?: {
    name?: string;
    email?: string;
    userId?: string;
    [key: string]: unknown;
  };
}

export default function Intercom({ appId, user }: IntercomProps) {
  useEffect(() => {
    // Initialize Intercom
    if (typeof window !== 'undefined') {
      window.intercomSettings = {
        api_base: 'https://api-iam.intercom.io',
        app_id: appId,
        ...user,
      };

      // Standard Intercom initialization code
      (function() {
        const w = window;
        const ic = w.Intercom;
        if (typeof ic === 'function') {
          ic('reattach_activator');
          ic('update', w.intercomSettings || {});
        } else {
          const d = document;
          const i = function(...args: unknown[]) {
            i.c(args);
          } as IntercomFunction;
          i.q = [];
          i.c = function(args: unknown[]) {
            i.q.push(args);
          };
          w.Intercom = i;
          const s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://widget.intercom.io/widget/' + appId;
          const x = d.getElementsByTagName('script')[0];
          if (x.parentNode) {
            x.parentNode.insertBefore(s, x);
          }
        }
      })();
    }

    // Cleanup function
    return () => {
      if (window.Intercom) {
        window.Intercom('shutdown');
        delete window.Intercom;
      }
    };
  }, [appId, user]);

  // This component doesn't render anything visible
  return null;
}
